import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EClaimCreationStatus, IClaimDetails, IClaimsListQuery } from '@/types';
import { twMerge } from 'tailwind-merge';
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from 'ui/mobile-ds';
import { ClaimsCard } from '@/components/Common';
import { Layout } from '@/components/Common/Layout';
import { useInView } from 'react-intersection-observer';
import {
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { claimsListQuery } from '@/queries/claims.queries';
import AppConfig from '@/AppConfig';
import { queryClient } from '@/context/CombinedProvider';
import { useAppContext } from '@/context/AppContext';
import clsx from 'clsx';
import HappyPlum from '@/assets/happy-plum.svg';
import { Loader } from '@/components/Common/Layout/Loader';
import toast from 'react-hot-toast';

const EmptyState = () => {
  return (
    <div className="flex flex-col gap-2 items-center max-w-[330px] mx-auto mt-14 justify-center">
      <img src={HappyPlum} />
      <p className="text-mobile-text-main text-[22px] font-bold leading-8 text-center">
        No claims yet
      </p>
      <p className="text-mobile-text-hierarchy-2 text-sm leading-5 text-center">
        No claims here - we're glad to see you're in the pink of health. If you
        need to file a new claim, begin below
      </p>
    </div>
  );
};

const TABS = [
  {
    label: 'Active',
    value: EClaimCreationStatus.ACTIVE,
    dataKey: 'activeClaims',
  },
  {
    label: 'Drafts',
    value: EClaimCreationStatus.DRAFTS,
    dataKey: 'draftClaims',
  },
  {
    label: 'Past',
    value: EClaimCreationStatus.PAST,
    dataKey: 'pastClaims',
  },
];

const ClaimsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultFilter =
    (searchParams.get('stageGroup') as EClaimCreationStatus) ||
    EClaimCreationStatus.ACTIVE;
  const [selectedFilter, setSelectedFilter] =
    useState<EClaimCreationStatus>(defaultFilter);

  const { isWebView } = useAppContext();

  const { ref: paginationRef, inView } = useInView();

  const {
    data: paginatedClaimsList,
    fetchNextPage,
    isLoading,
    status,
    isFetchingNextPage,
  } = useInfiniteQuery<
    IClaimsListQuery['claimsList'],
    unknown,
    InfiniteData<IClaimsListQuery['claimsList']>
  >({
    queryKey: claimsListQuery(selectedFilter).queryKey,
    //@ts-ignore
    queryFn: claimsListQuery(selectedFilter).queryFn,
    getPreviousPageParam: (firstPage) => {
      return firstPage?._links?.prev ? firstPage?.page - 1 : undefined;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?._links?.next ? lastPage?.page + 1 : undefined;
    },
    initialPageParam: 1,
  });

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  const prefetchClaims = async () => {
    const activeClaims = queryClient.prefetchInfiniteQuery<
      IClaimsListQuery,
      unknown,
      IClaimsListQuery,
      QueryKey,
      number
    >({
      queryKey: claimsListQuery(EClaimCreationStatus.ACTIVE).queryKey,
      // @ts-ignore
      queryFn: claimsListQuery(EClaimCreationStatus.ACTIVE).queryFn,
      initialPageParam: 1,
    });
    const draftClaims = queryClient.prefetchInfiniteQuery<
      IClaimsListQuery,
      unknown,
      IClaimsListQuery,
      QueryKey,
      number
    >({
      queryKey: claimsListQuery(EClaimCreationStatus.DRAFTS).queryKey,
      // @ts-ignore
      queryFn: claimsListQuery(EClaimCreationStatus.DRAFTS).queryFn,
      initialPageParam: 1,
    });
    const pastClaims = queryClient.prefetchInfiniteQuery<
      IClaimsListQuery,
      unknown,
      IClaimsListQuery,
      QueryKey,
      number
    >({
      queryKey: claimsListQuery(EClaimCreationStatus.PAST).queryKey,
      // @ts-ignore
      queryFn: claimsListQuery(EClaimCreationStatus.PAST).queryFn,
      initialPageParam: 1,
    });
    await Promise.allSettled([activeClaims, draftClaims, pastClaims]);
  };

  useEffect(() => {
    prefetchClaims();
  }, []);

  const handleOnFilterChange = (value: string) => {
    setSelectedFilter(value as EClaimCreationStatus);
    setSearchParams((params) => {
      setSearchParams(
        (params) => {
          params.set('stageGroup', value);
          return params;
        },
        {
          replace: true,
        }
      );
      return params;
    });
  };

  const navigate = useNavigate();

  const showNextPageSpinner = isFetchingNextPage && status === 'success';

  const handleClaimDeletion = async (claimId: string) => {
    toast.success(`Your claim ${claimId} was deleted successfully`);
  };

  return (
    <Layout.Main
      headerProps={{
        onBack: () => {
          navigate(AppConfig.HOME_PAGE);
        },
        backBarClassName: clsx({ 'fixed md:static top-0 z-10': isWebView }),
        className: clsx({ 'pt-12': isWebView }),
        renderTitleInBackBar: isWebView,
        title: 'All Claims',
        description: 'A list of your claims',
        rightAction: (
          <Link
            to={`${AppConfig.BASE_PATH}/claims/create`}
            className="hidden md:flex"
          >
            <Button className="w-full rounded-[12px] text-lg py-3 px-4">
              Start a claim
            </Button>
          </Link>
        ),
      }}
      footerProps={{
        isSticky: true,
        className: 'md:hidden',
        children: (
          <Link to={`${AppConfig.BASE_PATH}/claims/create`}>
            <Button className="w-full rounded-xl">Start a claim</Button>
          </Link>
        ),
      }}
      loading={isLoading}
    >
      {({ getDimensions, bg }) => {
        const backBarDimensions = getDimensions('claims-back-bar');

        return (
          <Tabs
            defaultValue={selectedFilter}
            value={selectedFilter}
            onValueChange={(value) => {
              handleOnFilterChange(value);
              window?.scrollTo({
                top: 0,
                behavior: 'smooth',
              });
            }}
          >
            <div
              className={clsx({
                'pb-2 mb-8  md:mb-10 sticky top-0 z-10 w-full mx-auto md:w-fit rounded-[46px] md:p-0':
                  true,
                [`bg-${bg}`]: bg,
              })}
              style={{
                top: backBarDimensions.height,
              }}
            >
              <TabsList
                className={
                  'bg-secondary-100 flex justify-center rounded-[46px] lg:w-fit lg:mx-auto shadow-md'
                }
              >
                {TABS?.map((tab) => {
                  return (
                    <TabsTrigger
                      key={tab?.value}
                      value={tab?.value}
                      className={twMerge(
                        'data-[state=active]:bg-white data-[state=active]:text-secondary-900 text-secondary-700 bg-transparent rounded-[46px] px-6 py-2 m-1.5 w-full lg:w-fit'
                      )}
                    >
                      {tab?.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>
            {TABS.map((tab) => {
              const combinedPaginatedData =
                paginatedClaimsList?.pages?.reduce(
                  (acc: Array<IClaimDetails>, curr) => {
                    return [...acc, ...(curr?.results ?? [])];
                  },
                  []
                ) ?? [];

              const hasNoClaims = (combinedPaginatedData?.length ?? 0) === 0;

              return (
                <TabsContent key={tab.value} value={tab.value}>
                  {hasNoClaims ? (
                    <EmptyState />
                  ) : (
                    <div className="flex flex-col lg:grid lg:grid-cols-2 pb-40 gap-4 md:gap-6">
                      {combinedPaginatedData.map((claim) => {
                        return (
                          <ClaimsCard
                            key={claim?.plumClaimId ?? claim?.insurerClaimId}
                            claimDetails={claim}
                            onDeletion={handleClaimDeletion}
                          />
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
              );
            })}
            {showNextPageSpinner ? <Loader /> : null}
            {!isLoading && status === 'success' && <div ref={paginationRef} />}
          </Tabs>
        );
      }}
    </Layout.Main>
  );
};

export default ClaimsList;
